import React, { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  Tooltip,
  IconButton,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
} from '@mui/material'
import {
  Launch as LaunchIcon,
  CheckCircle as DeliveredIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Pending as PendingIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import { shipmentAPI } from '../services/api'

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  try {
    return dayjs(dateStr).format('DD.MM.YYYY')
  } catch {
    return dateStr
  }
}

const getStatusChip = (status, trackingStatus) => {
  const trackingLower = (trackingStatus || '').toLowerCase()
  
  if (trackingLower.includes('delivered')) {
    return (
      <Chip
        icon={<DeliveredIcon />}
        label="Delivered"
        color="success"
        size="small"
        variant="filled"
      />
    )
  } else if (trackingLower.includes('error') || trackingLower.includes('failed')) {
    return (
      <Chip
        icon={<ErrorIcon />}
        label="Error"
        color="error"
        size="small"
        variant="filled"
      />
    )
  } else if (status === 'SHIPPED' || trackingLower.includes('transit')) {
    return (
      <Chip
        icon={<WarningIcon />}
        label="In Transit"
        color="warning"
        size="small"
        variant="filled"
      />
    )
  } else {
    return (
      <Chip
        icon={<PendingIcon />}
        label="Pending"
        color="default"
        size="small"
        variant="outlined"
      />
    )
  }
}

const getDaysColor = (daysStr) => {
  if (!daysStr) return 'text.primary'
  const days = parseInt(daysStr.replace('+', ''))
  if (days > 0) return 'success.main'
  if (days < 0) return 'error.main'
  return 'text.primary'
}

const ShipmentTable = ({ shipments, showAwb = true, onTrackingClick, onShipmentUpdate }) => {
  const [editingId, setEditingId] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)

  if (!shipments || shipments.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">
          No shipments found
        </Typography>
      </Box>
    )
  }

  const handleEdit = (shipment) => {
    setEditingId(shipment.id)
    setEditValue(shipment.tracking_status || '')
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditValue('')
  }

  const handleSave = async (shipmentId) => {
    setSaving(true)
    try {
      const updateData = {
        tracking_status: editValue.trim()
      }
      await shipmentAPI.updateShipment(shipmentId, updateData)
      setEditingId(null)
      setEditValue('')
      // Call parent callback to refresh data if provided
      if (onShipmentUpdate) {
        onShipmentUpdate()
      }
    } catch (error) {
      console.error('Failed to update shipment:', error)
      // Could add error notification here
    } finally {
      setSaving(false)
    }
  }

  // Common status options for dropdown
  const statusOptions = [
    'Pending',
    'Shipped',
    'In Transit',
    'Delivered',
    'Refunded',
  ]

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell><strong>Order ID</strong></TableCell>
            <TableCell><strong>Shipment ID</strong></TableCell>
            {showAwb && <TableCell><strong>AWB No</strong></TableCell>}
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Tracking ID</strong></TableCell>
            <TableCell><strong>First Scan Date</strong></TableCell>
            <TableCell><strong>Delivered Date</strong></TableCell>
            <TableCell><strong>Latest Delivery</strong></TableCell>
            <TableCell><strong>Days Taken</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shipments.map((shipment, index) => (
            <TableRow 
              key={shipment.id || index}
              sx={{ 
                '&:nth-of-type(odd)': { backgroundColor: 'grey.25' },
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {shipment.order_id}
                </Typography>
              </TableCell>
              
              <TableCell>
                <Typography variant="body2">
                  {shipment.shipment_id}
                </Typography>
              </TableCell>
              
              {showAwb && (
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {shipment.awb_no}
                  </Typography>
                </TableCell>
              )}
              
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                  {editingId === shipment.id ? (
                    <>
                      <Select
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        sx={{ flex: 1, minWidth: 120 }}
                        disabled={saving}
                        displayEmpty
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                        {/* Allow custom value if not in options */}
                        {editValue && !statusOptions.includes(editValue) && (
                          <MenuItem value={editValue}>{editValue}</MenuItem>
                        )}
                      </Select>
                      <Tooltip title="Save">
                        <IconButton 
                          size="small" 
                          onClick={() => handleSave(shipment.id)}
                          disabled={saving}
                          color="primary"
                        >
                          {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton 
                          size="small" 
                          onClick={handleCancel}
                          disabled={saving}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      {getStatusChip(shipment.status, shipment.tracking_status)}
                      <Tooltip title="Edit tracking status">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEdit(shipment)}
                          sx={{ ml: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </TableCell>
              
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {shipment.tracking_id || '-'}
                  </Typography>
                  {shipment.tracking_id && (
                    <Tooltip title="View tracking details">
                      <IconButton 
                        size="small" 
                        onClick={() => onTrackingClick && onTrackingClick(shipment.tracking_id)}
                      >
                        <LaunchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              
              <TableCell>
                <Typography variant="body2">
                  {formatDate(shipment.first_scan_date)}
                </Typography>
              </TableCell>
              
              <TableCell>
                <Typography variant="body2">
                  {formatDate(shipment.shipment_delivered_date)}
                </Typography>
              </TableCell>
              
              <TableCell>
                <Typography variant="body2">
                  {formatDate(shipment.latest_delivery_date)}
                </Typography>
              </TableCell>
              
              <TableCell>
                <Typography 
                  variant="body2" 
                  fontWeight="medium"
                  color={getDaysColor(shipment.days_taken)}
                >
                  {shipment.days_taken || '-'}
                </Typography>
              </TableCell>
              
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {/* Summary Row */}
      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="textSecondary">
          Showing {shipments.length} shipment{shipments.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </TableContainer>
  )
}

export default ShipmentTable